// 自定义产品顺序 — per-user personalization for 当日现货.
//
// Flow (locked with Regina): check the products you want surfaced, then arrange
// them with ↑/↓ into the order you like. Pinned products float to the top of
// the grid in this exact order; everything you don't pin still shows below in
// the normal sort. Saved per-account (syncs across devices).
//
// ↑/↓ instead of drag on purpose — store staff use tablets, and touch-drag in a
// scrollable list is finicky; arrow buttons are unambiguous and work everywhere.

import { useMemo, useState } from 'react';
import { displayName } from '../lib/products.ts';
import { Icon } from './Icon.jsx';

export const CustomizeOrderModal = ({ products = [], initialOrder = [], onSave, onClose }) => {
  const byId = useMemo(() => new Map(products.map(p => [p.id, p])), [products]);

  // pinned = ordered list of product ids, filtered to ones that still exist.
  const [pinned, setPinned] = useState(() => initialOrder.filter(id => byId.has(id)));
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const pinnedSet = useMemo(() => new Set(pinned), [pinned]);

  // Unpinned products, filtered by search, sorted by category then name.
  const unpinned = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products
      .filter(p => !pinnedSet.has(p.id))
      .filter(p => !q ||
        p.name.toLowerCase().includes(q) ||
        (p.category || '').toLowerCase().includes(q) ||
        (p.variant || '').toLowerCase().includes(q))
      .sort((a, b) =>
        (a.category || '').localeCompare(b.category || '', 'zh') ||
        a.name.localeCompare(b.name, 'zh'));
  }, [products, pinnedSet, search]);

  function pin(id)   { setPinned(prev => [...prev, id]); }
  function unpin(id) { setPinned(prev => prev.filter(x => x !== id)); }
  function move(idx, dir) {
    setPinned(prev => {
      const next = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  }

  async function save() {
    setSaving(true); setErr('');
    try {
      await onSave(pinned);
      onClose();
    } catch (e) {
      setErr(e?.message || '保存失败');
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop" role="dialog" aria-labelledby="cust-order-title"
         onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal cust-order-modal">
        <h2 className="modal-title" id="cust-order-title">自定义产品顺序</h2>
        <p className="cust-order-hint">
          勾选常用产品置顶，用 ↑ ↓ 排成你习惯的顺序。没勾选的仍会显示在下方。只影响你自己的视图。
        </p>

        {/* Pinned (ordered) */}
        <div className="cust-order-section">
          <div className="cust-order-section-head">已置顶 <span className="mono">{pinned.length}</span></div>
          {pinned.length === 0 ? (
            <div className="cust-order-empty">还没有置顶产品 · 从下面勾选要常用的</div>
          ) : (
            <ul className="cust-order-list pinned">
              {pinned.map((id, i) => {
                const p = byId.get(id);
                if (!p) return null;
                return (
                  <li key={id} className="cust-order-row">
                    <div className="cust-order-rank mono">{i + 1}</div>
                    <div className="cust-order-name" title={p.name}>
                      {p.category && <span className="inv-name-sub">{p.category} · </span>}
                      {displayName(p)}
                    </div>
                    <div className="cust-order-moves">
                      <button type="button" aria-label="上移" disabled={i === 0}
                              onClick={() => move(i, -1)}>↑</button>
                      <button type="button" aria-label="下移" disabled={i === pinned.length - 1}
                              onClick={() => move(i, 1)}>↓</button>
                      <button type="button" className="cust-order-unpin" aria-label="取消置顶"
                              onClick={() => unpin(id)}>✕</button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* All other products */}
        <div className="cust-order-section">
          <div className="cust-order-section-head">全部产品 <span className="mono">{products.length - pinned.length}</span></div>
          <input className="vava-input cust-order-search" type="text"
                 placeholder="搜产品…（草莓 / 6寸 / 胚）"
                 value={search} onChange={(e) => setSearch(e.target.value)} />
          <ul className="cust-order-list all">
            {unpinned.length === 0 ? (
              <li className="cust-order-empty">{search ? '没有匹配的产品' : '全部产品都已置顶'}</li>
            ) : unpinned.map(p => (
              <li key={p.id} className="cust-order-row">
                <label className="cust-order-check">
                  <input type="checkbox" checked={false} onChange={() => pin(p.id)} />
                  <span className="cust-order-name" title={p.name}>
                    {p.category && <span className="inv-name-sub">{p.category} · </span>}
                    {displayName(p)}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </div>

        {err && <div className="login-error" style={{ marginTop: 10 }}>⚠ {err}</div>}
        <div className="cust-order-foot">
          {pinned.length > 0 && (
            <button type="button" className="btn btn-ghost cust-order-clear"
                    onClick={() => setPinned([])} disabled={saving}>清空置顶</button>
          )}
          <span style={{ flex: 1 }} />
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>取消</button>
          <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>
            <Icon name="check" />{saving ? '保存中…' : '保存'}
          </button>
        </div>
      </div>
    </div>
  );
};
